import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Extend RouteMeta type
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresPermission?: string
    allowedRoles?: string[]
    title?: string
  }
}

// Views
import LoginView from '../views/LoginView.vue'
import RegisterView from '../views/RegisterView.vue'
import ForgotPasswordView from '../views/ForgotPasswordView.vue'
import ResetPasswordView from '../views/ResetPasswordView.vue'
import ActivateAccountView from '../views/ActivateAccountView.vue'
import EditProfileView from '../views/EditProfileView.vue'
import ClubView from '../views/ClubView.vue'
import EditClubView from '../views/EditClubView.vue'
import BankAccountsView from '../views/BankAccountsView.vue'
import AddBankAccountView from '../views/AddBankAccountView.vue'
import EditBankAccountView from '../views/EditBankAccountView.vue'
import CategoriesView from '../views/CategoriesView.vue'
import AddCategoryView from '../views/AddCategoryView.vue'
import EditCategoryView from '../views/EditCategoryView.vue'
import CategoryDetailView from '../views/CategoryDetailView.vue'
import CoachesView from '../views/CoachesView.vue'
import AddCoachView from '../views/AddCoachView.vue'
import EditCoachView from '../views/EditCoachView.vue'
import CoachDetailView from '../views/CoachDetailView.vue'
import DashboardView from '../views/DashboardView.vue'
import AddPlayerView from '../views/AddPlayerView.vue'
import PlayerImportMappingView from '../views/PlayerImportMappingView.vue'
import PlayerImportReviewView from '../views/PlayerImportReviewView.vue'
import EditPlayerView from '../views/EditPlayerView.vue'
import PlayerDetailView from '../views/PlayerDetailView.vue'
import EventsView from '../views/EventsView.vue'
import AddEventView from '../views/AddEventView.vue'
import AddEventV2View from '../views/AddEventV2View.vue'
import EditEventView from '../views/EditEventView.vue'
import EventDetailView from '../views/EventDetailView.vue'
import EventCyclesView from '../views/EventCyclesView.vue'
import EditEventCycleView from '../views/EditEventCycleView.vue'
import AddAttendanceView from '../views/AddAttendanceView.vue'
import EditAttendanceView from '../views/EditAttendanceView.vue'
import AddPaymentView from '../views/AddPaymentView.vue'
import EditPaymentView from '../views/EditPaymentView.vue'
import PaymentsView from '../views/PaymentsView.vue'
import RecurringPaymentsView from '../views/RecurringPaymentsView.vue'
import EditRecurringPaymentView from '../views/EditRecurringPaymentView.vue'
import PublicPaymentView from '../views/PublicPaymentView.vue'
import KlarnaCheckoutView from '../views/KlarnaCheckoutView.vue'
import NotificationsView from '../views/NotificationsView.vue'
import ChooseProfileView from '../views/ChooseProfileView.vue'
import ConversationsView from '../views/ConversationsView.vue'
import ConversationView from '../views/ConversationView.vue'
import PaymentReminderTemplateView from '../views/PaymentReminderTemplateView.vue'
import PaymentSettingsView from '../views/PaymentSettingsView.vue'
import PaymentDocumentCreateView from '../views/PaymentDocumentCreateView.vue'
import PaymentDocumentEditView from '../views/PaymentDocumentEditView.vue'
import PaymentMethodCreateView from '../views/PaymentMethodCreateView.vue'
import PaymentMethodEditView from '../views/PaymentMethodEditView.vue'
import FinancialStatsView from '../views/FinancialStatsView.vue'
import PaymentSuccessView from '../views/PaymentSuccessView.vue'
import PaymentCancelView from '../views/PaymentCancelView.vue'
import PaymentDocumentHistoryView from '../views/PaymentDocumentHistoryView.vue'
import PaymentAccessListView from '../views/PaymentAccessListView.vue'
import PublicAccessPaymentView from '../views/PublicAccessPaymentView.vue'
import TermsView from '../views/TermsView.vue'
import PrivacyPolicyView from '../views/PrivacyPolicyView.vue'
import PaymentTermsView from '../views/PaymentTermsView.vue'
import RegisterClubView from '../views/RegisterClubView.vue'
import NoAccessView from '../views/NoAccessView.vue'
import ForceUpdateView from '../views/ForceUpdateView.vue'
import DemoLoadingView from '../views/DemoLoadingView.vue'
import JoinClubView from '../views/JoinClubView.vue'
import ClubApplicationsView from '../views/ClubApplicationsView.vue'
import ClubApplicationDetailView from '../views/ClubApplicationDetailView.vue'
import { Capacitor } from '@capacitor/core'
import env from '../config/env'
import i18n from '../i18n'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    redirect: () => {
      // Redirect based on user role
      const authStore = useAuthStore()

      // Demo user still activating — hold them on the loading screen
      if (authStore.isDemo && authStore.demoStatus === 'pending') {
        return '/demo-loading'
      }

      const currentRole = authStore.initData?.persons?.current?.role

      if (currentRole === 'PLAYER') {
        return '/events'
      } else if (currentRole === 'OWNER' || currentRole === 'SUPERVISOR' || currentRole === 'COACH') {
        return '/dashboard'
      }

      // Default fallback
      return '/dashboard'
    }
  },
  {
    path: '/choose-profile',
    name: 'choose-profile',
    component: ChooseProfileView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.chooseProfile'
    }
  },
  {
    path: '/register-club',
    name: 'register-club',
    component: RegisterClubView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.registerClub'
    }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.login'
    }
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.register'
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.forgotPassword'
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.resetPassword'
    }
  },
  {
    path: '/activate',
    name: 'activate-account',
    component: ActivateAccountView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.activateAccount'
    }
  },
  {
    path: '/join/:token',
    name: 'join-club',
    component: JoinClubView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.joinClub'
    }
  },
  {
    path: '/payment/:uuid/:hash',
    name: 'public-payment',
    component: PublicPaymentView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.payment'
    }
  },
  {
    path: '/payment/:uuid/:hash/klarna-checkout',
    name: 'klarna-checkout',
    component: KlarnaCheckoutView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.paymentKlarna'
    }
  },
  {
    path: '/public/access-payment/:hash',
    name: 'public-access-payment',
    component: PublicAccessPaymentView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.accessPayment'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: TermsView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.terms'
    }
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: PrivacyPolicyView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.privacyPolicy'
    }
  },
  {
    path: '/payment-terms',
    name: 'payment-terms',
    component: PaymentTermsView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.paymentTerms'
    }
  },
  {
    path: '/no-access',
    name: 'no-access',
    component: NoAccessView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.noAccess'
    }
  },
  {
    path: '/force-update',
    name: 'force-update',
    component: ForceUpdateView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.forceUpdate'
    }
  },
  {
    path: '/demo-loading',
    name: 'demo-loading',
    component: DemoLoadingView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.demoLoading'
    }
  },
  {
    path: '/edit-profile',
    name: 'edit-profile',
    component: EditProfileView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.editProfile'
    }
  },
  {
    path: '/club/:uuid',
    name: 'club',
    component: ClubView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.club'
    }
  },
  {
    path: '/club/:uuid/edit',
    name: 'edit-club',
    component: EditClubView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.editClub'
    }
  },
  {
    path: '/club/:uuid/bank-accounts',
    name: 'bank-accounts',
    component: BankAccountsView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.bankAccounts'
    }
  },
  {
    path: '/club/:uuid/bank-accounts/create',
    name: 'add-bank-account',
    component: AddBankAccountView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.addBankAccount'
    }
  },
  {
    path: '/club/:uuid/bank-accounts/:accountUuid/edit',
    name: 'edit-bank-account',
    component: EditBankAccountView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.editBankAccount'
    }
  },
  {
    path: '/club/:uuid/payment-reminder-template',
    name: 'payment-reminder-template',
    component: PaymentReminderTemplateView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.editPaymentReminderTemplate'
    }
  },
  {
    path: '/club/:uuid/payment-settings',
    name: 'payment-settings',
    component: PaymentSettingsView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.paymentSettings'
    }
  },
  {
    path: '/club/:uuid/financial-stats',
    name: 'financial-stats',
    component: FinancialStatsView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.financialStats',
      requiresRole: ['OWNER', 'SUPERVISOR']
    }
  },
  {
    path: '/club/:uuid/payment-documents/:type/create',
    name: 'payment-document-create',
    component: PaymentDocumentCreateView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.addPaymentDocument'
    }
  },
  {
    path: '/club/:uuid/payment-documents/:documentUuid/edit',
    name: 'payment-document-edit',
    component: PaymentDocumentEditView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.editPaymentDocument'
    }
  },
  {
    path: '/club/:uuid/payment-documents/history/:type',
    name: 'payment-document-history',
    component: PaymentDocumentHistoryView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.paymentDocumentHistory'
    }
  },
  {
    path: '/club/:uuid/payment-methods/create',
    name: 'payment-method-create',
    component: PaymentMethodCreateView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.addPaymentMethod'
    }
  },
  {
    path: '/club/:uuid/payment-methods/edit',
    name: 'payment-method-edit',
    component: PaymentMethodEditView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.editPaymentMethod'
    }
  },
  {
    path: '/club/:uuid/payment-success',
    name: 'payment-success',
    component: PaymentSuccessView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.paymentSuccess'
    }
  },
  {
    path: '/club/:uuid/payment-cancel',
    name: 'payment-cancel',
    component: PaymentCancelView,
    meta: {
      requiresAuth: false,
      title: 'routeTitles.paymentCancelled'
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PLAYER_INDEX',
      allowedRoles: ['OWNER', 'SUPERVISOR', 'COACH'],
      title: 'routeTitles.dashboard'
    }
  },
  {
    path: '/categories',
    name: 'categories',
    component: CategoriesView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'CATEGORY_INDEX',
      title: 'routeTitles.categories'
    }
  },
  {
    path: '/categories/create',
    name: 'add-category',
    component: AddCategoryView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'CATEGORY_STORE',
      title: 'routeTitles.addCategory'
    }
  },
  {
    path: '/categories/:uuid',
    name: 'category-detail',
    component: CategoryDetailView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'CATEGORY_SHOW',
      title: 'routeTitles.category'
    }
  },
  {
    path: '/categories/:uuid/edit',
    name: 'edit-category',
    component: EditCategoryView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'CATEGORY_UPDATE',
      title: 'routeTitles.editCategory'
    }
  },
  {
    path: '/coaches',
    name: 'coaches',
    component: CoachesView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'COACH_INDEX',
      title: 'routeTitles.coaches'
    }
  },
  {
    path: '/coaches/create',
    name: 'add-coach',
    component: AddCoachView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'COACH_STORE',
      title: 'routeTitles.addCoach'
    }
  },
  {
    path: '/coaches/:uuid',
    name: 'coach-detail',
    component: CoachDetailView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'COACH_SHOW',
      title: 'routeTitles.coach'
    }
  },
  {
    path: '/coaches/:uuid/edit',
    name: 'edit-coach',
    component: EditCoachView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'COACH_UPDATE',
      title: 'routeTitles.editCoach'
    }
  },
  {
    path: '/players/create',
    name: 'add-player',
    component: AddPlayerView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PLAYER_CREATE',
      title: 'routeTitles.addPlayer'
    }
  },
  {
    path: '/players/import/mapping',
    name: 'player-import-mapping',
    component: PlayerImportMappingView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PLAYER_IMPORT',
      title: 'routeTitles.playerImport'
    }
  },
  {
    path: '/players/import/review',
    name: 'player-import-review',
    component: PlayerImportReviewView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PLAYER_IMPORT',
      title: 'routeTitles.playerImport'
    }
  },
  {
    path: '/club-applications',
    name: 'club-applications',
    component: ClubApplicationsView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'CLUB_APPLICATION_INDEX',
      allowedRoles: ['OWNER', 'SUPERVISOR', 'COACH'],
      title: 'routeTitles.clubApplications'
    }
  },
  {
    path: '/club-applications/:uuid',
    name: 'club-application-detail',
    component: ClubApplicationDetailView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'CLUB_APPLICATION_INDEX',
      allowedRoles: ['OWNER', 'SUPERVISOR', 'COACH'],
      title: 'routeTitles.clubApplication'
    }
  },
  {
    path: '/players/:uuid',
    name: 'player-detail',
    component: PlayerDetailView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PLAYER_SHOW',
      title: 'routeTitles.playerProfile'
    }
  },
  {
    path: '/players/:uuid/edit',
    name: 'edit-player',
    component: EditPlayerView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PLAYER_UPDATE',
      title: 'routeTitles.editPlayer'
    }
  },
  {
    path: '/events',
    name: 'events',
    component: EventsView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_INDEX',
      title: 'routeTitles.events'
    }
  },
  {
    path: '/events/cycles',
    name: 'event-cycles',
    component: EventCyclesView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_CYCLES_INDEX',
      allowedRoles: ['OWNER', 'SUPERVISOR', 'COACH'],
      title: 'routeTitles.eventCycles'
    }
  },
  {
    path: '/events/:uuid/cycle/edit',
    name: 'edit-event-cycle',
    component: EditEventCycleView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_CYCLE_UPDATE',
      title: 'routeTitles.editEventCycle'
    }
  },
  {
    path: '/events/create',
    name: 'add-event',
    component: AddEventView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_CREATE',
      title: 'routeTitles.addEvent'
    }
  },
  {
    path: '/events/create2',
    name: 'add-event-v2',
    component: AddEventV2View,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_CREATE',
      title: 'routeTitles.addEvent'
    }
  },
  {
    path: '/events/:uuid/edit',
    name: 'edit-event',
    component: EditEventView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_UPDATE',
      title: 'routeTitles.editEvent'
    }
  },
  {
    path: '/events/:uuid',
    name: 'event-detail',
    component: EventDetailView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_SHOW',
      title: 'routeTitles.eventDetails'
    }
  },
  {
    path: '/events/:uuid/attendance/add',
    name: 'add-attendance',
    component: AddAttendanceView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_SHOW',
      title: 'routeTitles.checkAttendance'
    }
  },
  {
    path: '/events/:uuid/attendance/edit',
    name: 'edit-attendance',
    component: EditAttendanceView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'EVENT_ATTENDANCE_UPDATE',
      title: 'routeTitles.editAttendance'
    }
  },
  {
    path: '/payments',
    name: 'payments',
    component: PaymentsView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PAYMENT_INDEX',
      allowedRoles: ['OWNER', 'SUPERVISOR', 'PLAYER'],
      title: 'routeTitles.payments'
    }
  },
  {
    path: '/payments/add',
    name: 'add-payment',
    component: AddPaymentView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PAYMENT_STORE',
      allowedRoles: ['OWNER', 'SUPERVISOR'],
      title: 'routeTitles.addPayment'
    }
  },
  {
    path: '/payments/:uuid/edit',
    name: 'edit-payment',
    component: EditPaymentView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PAYMENT_UPDATE',
      allowedRoles: ['OWNER', 'SUPERVISOR'],
      title: 'routeTitles.editPayment'
    }
  },
  {
    path: '/recurring-payments',
    name: 'recurring-payments',
    component: RecurringPaymentsView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PAYMENT_INDEX',
      allowedRoles: ['OWNER', 'SUPERVISOR'],
      title: 'routeTitles.recurringPayments'
    }
  },
  {
    path: '/recurring-payments/:uuid/edit',
    name: 'edit-recurring-payment',
    component: EditRecurringPaymentView,
    meta: {
      requiresAuth: true,
      requiresPermission: 'PAYMENT_UPDATE',
      allowedRoles: ['OWNER', 'SUPERVISOR'],
      title: 'routeTitles.editRecurringPayment'
    }
  },
  {
    path: '/payment-access',
    name: 'payment-access',
    component: PaymentAccessListView,
    meta: {
      requiresAuth: true,
      allowedRoles: ['OWNER', 'SUPERVISOR'],
      title: 'routeTitles.appAccess'
    }
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: NotificationsView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.notifications'
    }
  },
  {
    path: '/notifications/:clubUuid/:notificationUuid',
    name: 'notification-detail',
    component: () => import('../views/NotificationView.vue'),
    meta: {
      requiresAuth: true,
      title: 'routeTitles.notification'
    }
  },
  {
    path: '/conversations',
    name: 'conversations',
    component: ConversationsView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.messages'
    }
  },
  {
    path: '/conversations/:uuid',
    name: 'conversation',
    component: ConversationsView,
    meta: {
      requiresAuth: true,
      title: 'routeTitles.conversation'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return false
  }
})

// Navigation guards
let authInitialized = false

router.beforeEach(async (to, from, next) => {
  if (!to.hash) {
    const appEl = document.getElementById('app')
    if (appEl) appEl.scrollTop = 0
    window.scrollTo(0, 0)
  }

  const authStore = useAuthStore()

  // Initialize auth store once (load user/token from localStorage)
  if (!authInitialized) {
    authStore.initAuth()
    authInitialized = true

    // If user is authenticated, refresh init data from API
    if (authStore.isAuthenticated && authStore.user?.uuid) {
      try {
        await authStore.initUserData(authStore.user.uuid)
      } catch (err) {
        console.error('Failed to refresh init data on page load:', err)
      }
    }
  }

  // Version check for native apps only
  if (Capacitor.isNativePlatform() && authStore.isAuthenticated && authStore.initData?.front_app_version) {
    const requiredVersion = authStore.initData.front_app_version
    const currentVersion = env.app.version
    if (requiredVersion !== currentVersion && to.name !== 'force-update') {
      next({ name: 'force-update' })
      return
    }
  }

  // If on force-update but version is OK or not native, redirect away
  if (to.name === 'force-update') {
    if (!Capacitor.isNativePlatform()) {
      next({ name: 'home' })
      return
    }
    const requiredVersion = authStore.initData?.front_app_version
    const currentVersion = env.app.version
    if (!requiredVersion || requiredVersion === currentVersion) {
      next({ name: 'home' })
      return
    }
  }

  // Set page title
  // Ensure i18n locale is synced with localStorage before setting title
  const storedLanguage = localStorage.getItem('language')
  if (storedLanguage && i18n.global.locale.value !== storedLanguage) {
    i18n.global.locale.value = storedLanguage
  }

  if (to.meta.title) {
    const t = i18n.global.t as (key: string) => string
    document.title = `${t(to.meta.title)} - sportovo.io`
  } else {
    document.title = 'sportovo.io'
  }

  // Check if user needs profile setup (no role, no permissions, no club)
  // Only check if initData is loaded (not null)
  const currentRole = authStore.initData?.persons?.current?.role
  const permissions = authStore.initData?.permission_model || []
  const clubUuid = authStore.initData?.club?.uuid
  const needsProfileSetup = authStore.isAuthenticated && authStore.initData && !currentRole && permissions.length === 0 && !clubUuid

  // Debug logging
  if (authStore.isAuthenticated && import.meta.env.DEV) {
    console.log('[Router Guard] Profile setup check:', {
      isAuthenticated: authStore.isAuthenticated,
      hasInitData: !!authStore.initData,
      currentRole,
      permissionsCount: permissions.length,
      clubUuid,
      needsProfileSetup,
      to: to.name
    })
  }

  // Redirect to choose-profile if user needs setup (except when already on that page, edit-profile, or register-club)
  if (needsProfileSetup && to.name !== 'choose-profile' && to.name !== 'edit-profile' && to.name !== 'register-club' && to.name !== 'login' && to.name !== 'register' && to.meta.requiresAuth !== false) {
    next({ name: 'choose-profile' })
    return
  }

  // Don't allow access to choose-profile if user doesn't need setup
  if (to.name === 'choose-profile' && !needsProfileSetup && authStore.isAuthenticated) {
    next({ name: 'home' })
    return
  }

  // Redirect OWNER/SUPERVISOR away from no-access to payment-access
  if (to.name === 'no-access' && (currentRole === 'OWNER' || currentRole === 'SUPERVISOR')) {
    next({ name: 'payment-access' })
    return
  }

  // Check club access expiry — if access_to is more than 7 days in the past
  const exemptRoutes = ['no-access', 'payment-access', 'force-update', 'login', 'register', 'choose-profile', 'register-club', 'edit-profile', 'public-access-payment', 'public-payment', 'terms', 'privacy-policy', 'payment-terms', 'join-club']
  if (authStore.isAuthenticated && currentRole && !exemptRoutes.includes(to.name as string)) {
    const accessDateTo = authStore.initData?.subscriptions?.date_to
    if (accessDateTo) {
      const accessDate = new Date(accessDateTo)
      const today = new Date()
      const diffTime = today.getTime() - accessDate.getTime()
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays > 7) {
        // Access expired more than 7 days ago
        if (currentRole === 'OWNER' || currentRole === 'SUPERVISOR') {
          next({ name: 'payment-access' })
          return
        } else {
          // COACH or PLAYER
          next({ name: 'no-access' })
          return
        }
      }
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login if not authenticated
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (
    (to.name === 'login' || to.name === 'register') &&
    authStore.isAuthenticated
  ) {
    // Redirect to home if already authenticated and trying to access login/register
    next({ name: 'home' })
  } else if (to.name === 'club') {
    // Check if user's club UUID matches route param
    const routeClubUuid = to.params.uuid as string
    const userClubUuid = authStore.initData?.club?.uuid

    if (!userClubUuid || routeClubUuid !== userClubUuid) {
      // User doesn't belong to this club
      console.error(`Access denied: User does not belong to club ${routeClubUuid}`)
      next({ name: 'home' })
      return
    }
    next()
  } else if (to.name === 'edit-club') {
    // Check if user's club UUID matches route param
    const routeClubUuid = to.params.uuid as string
    const userClubUuid = authStore.initData?.club?.uuid
    const currentRole = authStore.initData?.persons?.current?.role

    if (!userClubUuid || routeClubUuid !== userClubUuid) {
      // User doesn't belong to this club
      console.error(`Access denied: User does not belong to club ${routeClubUuid}`)
      next({ name: 'home' })
      return
    }

    if (currentRole !== 'OWNER' && currentRole !== 'SUPERVISOR') {
      // User doesn't have permission to edit club
      console.error(`Access denied: User role ${currentRole} cannot edit club`)
      next({ name: 'club', params: { uuid: routeClubUuid } })
      return
    }
    next()
  } else if (to.name === 'bank-accounts' || to.name === 'add-bank-account' || to.name === 'edit-bank-account' || to.name === 'payment-reminder-template') {
    // Check if user's club UUID matches route param
    const routeClubUuid = to.params.uuid as string
    const userClubUuid = authStore.initData?.club?.uuid
    const currentRole = authStore.initData?.persons?.current?.role

    if (!userClubUuid || routeClubUuid !== userClubUuid) {
      // User doesn't belong to this club
      console.error(`Access denied: User does not belong to club ${routeClubUuid}`)
      next({ name: 'home' })
      return
    }

    if (currentRole !== 'OWNER' && currentRole !== 'SUPERVISOR') {
      // User doesn't have permission to manage bank accounts or payment templates
      console.error(`Access denied: User role ${currentRole} cannot manage bank accounts or payment templates`)
      next({ name: 'club', params: { uuid: routeClubUuid } })
      return
    }
    next()
  } else if (to.meta.allowedRoles) {
    // Check if user has allowed role
    const allowedRoles = to.meta.allowedRoles as string[]
    const currentRole = authStore.initData?.persons?.current?.role

    if (!currentRole || !allowedRoles.includes(currentRole)) {
      // Demo user in the middle of activation — hold them on /demo-loading instead of looping
      if (authStore.isDemo && authStore.demoStatus === 'pending') {
        if (to.name !== 'demo-loading') next({ name: 'demo-loading' })
        else next()
        return
      }

      // User doesn't have allowed role - redirect based on role
      console.error(`Access denied: User role ${currentRole} is not in allowed roles [${allowedRoles.join(', ')}]`)

      if (currentRole === 'PLAYER') {
        next({ name: 'events' })
      } else if (currentRole === 'OWNER' || currentRole === 'SUPERVISOR' || currentRole === 'COACH') {
        if (to.name === 'dashboard') next()
        else next({ name: 'dashboard' })
      } else {
        // No role or unknown role - stop the loop instead of endlessly redirecting to dashboard
        if (to.name === 'dashboard') next()
        else next({ name: 'dashboard' })
      }
      return
    }

    // Also check permission if specified
    if (to.meta.requiresPermission) {
      const requiredPermission = to.meta.requiresPermission as string
      const permissions = authStore.initData?.permission_model || []

      if (!permissions.includes(requiredPermission)) {
        console.error(`Access denied: Missing permission ${requiredPermission}`)

        if (currentRole === 'PLAYER') {
          next({ name: 'events' })
        } else if (currentRole === 'OWNER' || currentRole === 'SUPERVISOR' || currentRole === 'COACH') {
          if (to.name === 'dashboard') next()
          else next({ name: 'dashboard' })
        } else {
          if (to.name === 'dashboard') next()
          else next({ name: 'dashboard' })
        }
        return
      }
    }

    next()
  } else if (to.meta.requiresPermission) {
    // Check if user has required permission
    const requiredPermission = to.meta.requiresPermission as string
    const permissions = authStore.initData?.permission_model || []
    const currentRole = authStore.initData?.persons?.current?.role

    if (!permissions.includes(requiredPermission)) {
      // Demo user in the middle of activation — hold them on /demo-loading instead of looping
      if (authStore.isDemo && authStore.demoStatus === 'pending') {
        if (to.name !== 'demo-loading') next({ name: 'demo-loading' })
        else next()
        return
      }

      // User doesn't have permission - redirect based on role
      console.error(`Access denied: Missing permission ${requiredPermission}`)

      if (currentRole === 'PLAYER') {
        next({ name: 'events' })
      } else if (currentRole === 'OWNER' || currentRole === 'SUPERVISOR' || currentRole === 'COACH') {
        if (to.name === 'dashboard') next()
        else next({ name: 'dashboard' })
      } else {
        if (to.name === 'dashboard') next()
        else next({ name: 'dashboard' })
      }
      return
    }
    next()
  } else {
    next()
  }
})

const GA_CONVERSION_ID = 'G-LCGYKKWC5B'
const GA_CONVERSION_ROUTES = new Set(['login', 'register'])

router.afterEach((to) => {
  if (!GA_CONVERSION_ROUTES.has(to.name as string)) return

  if (typeof window.gtag !== 'function') {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_CONVERSION_ID}`
    document.head.appendChild(script)
    script.onload = () => {
      window.dataLayer = window.dataLayer || []
      window.gtag = function () { window.dataLayer.push(arguments) }
      window.gtag('js', new Date())
      window.gtag('config', GA_CONVERSION_ID, { page_path: to.fullPath })
    }
    return
  }

  window.gtag('config', GA_CONVERSION_ID, { page_path: to.fullPath })
})

export default router
